import { useState } from 'react'
import { styled } from '@mui/material/styles'
import Modal from '@mui/material/Modal'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import LoadingButton from '@mui/lab/LoadingButton'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import {
  useParams,
  useNavigate,
  useSearchParams,
  useSubmit,
  useLoaderData,
  useActionData,
} from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { adminSponsorshipByIdLoader } from '../../Admin.loader'
import { getOption, useSponsorshipStatusOptions, useLaboratoriesOptions } from '../../../../utils'
import { LoaderData, SponsorshipStatus, Sponsorship } from '../../../../types'
import { putParticipant } from '../../Admin.action'

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  borderRadius: { xs: 0, sm: 6 },
  boxShadow: 24,
  padding: { xs: 1, sm: 3 },
  width: { xs: '100%', sm: '90%' },
  maxWidth: '50rem',
  height: { xs: '100%', sm: '80%' },
  overflow: 'auto',
}

const Title = styled('h2')(() => ({
  marginBottom: 0,
}))
const Form = styled('form')(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'space-between',
  flex: '1 0 auto',
}))

export default function EditSponsorship() {
  const { t } = useTranslation('admin')
  const submit = useSubmit()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const actionData = useActionData() as LoaderData<ReturnType<typeof putParticipant>> | undefined
  const { sponsorshipId, eventId } = useParams()
  if (!eventId) {
    throw new Error('eventId is required')
  }

  const sponsorshipStatusOptions = useSponsorshipStatusOptions(t)
  const laboratoriesOptions = useLaboratoriesOptions()

  const { sponsorship } = useLoaderData() as LoaderData<
    ReturnType<typeof adminSponsorshipByIdLoader>
  >

  const handleClose = () => {
    navigate(`/admin/details/${eventId}/sponsorship?${searchParams.toString()}`)
  }

  const onSubmitForm = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const formData = new FormData(event.target as HTMLFormElement)
    const status =
      getOption(sponsorshipStatusOptions, formData.get('status') as string)?.value ??
      SponsorshipStatus.Pending
    const laboratory = formData.get('laboratory')
      ? Number(formData.get('laboratory') as string)
      : null

    const parsedData: Pick<Sponsorship<true>, 'status' | 'laboratory'> = {
      laboratory,
      status,
    }

    // TODO use zod validation and schema
    submit(parsedData, {
      method: 'POST',
      encType: 'application/json',
      action: `/admin/details/${eventId}/sponsorship/${sponsorshipId}?${searchParams.toString()}`,
    })
  }

  return (
    <Modal open={true} onClose={handleClose}>
      <Stack sx={style}>
        <Form onSubmit={onSubmitForm}>
          <Stack>
            <Title>{t('Modifier une prise en charge')}</Title>
            <Divider />
          </Stack>
          <Stack gap="1rem" flex="1 0" margin="1.5rem 0">
            <Autocomplete
              disablePortal
              id="status-autocomplete"
              options={sponsorshipStatusOptions}
              fullWidth
              disableClearable
              defaultValue={
                getOption(sponsorshipStatusOptions, undefined, sponsorship?.status) ?? undefined
              }
              renderInput={params => (
                <TextField {...params} label={t('Statut de prise en charge')} name="status" />
              )}
            />
            <Autocomplete
              disablePortal
              id="laboratory-autocomplete"
              options={laboratoriesOptions}
              fullWidth
              disableClearable
              defaultValue={
                getOption(laboratoriesOptions, undefined, sponsorship?.laboratory) ?? undefined
              }
              renderInput={params => (
                <TextField {...params} label={t('Laboratoire')} name="laboratory" />
              )}
            />
          </Stack>
          <Stack direction="row" justifyContent="space-between" paddingBottom="1.5rem">
            <Button
              onClick={e => {
                e.preventDefault()
                handleClose()
              }}
            >
              {t('Annuler')}
            </Button>
            <LoadingButton variant="contained" type="submit">
              {t('Modifier')}
            </LoadingButton>
          </Stack>
        </Form>
      </Stack>
    </Modal>
  )
}
